import { useMemo } from 'react'

import type { UseFiltersPayload } from '@domain-group/react-hooks'
import { useModals, useNumericParams } from '@domain-group/react-hooks'

import { ModalContext } from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'

import handleCallbackAction from '@functions/handleCallbackAction'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import useSetState from '@hooks/useSetState'

import BulkCreateAdAccountGroupLinksModal from '@modals/BulkCreateAdAccountGroupLinksModal'

import type { AdAccountGroupLinkModel } from '@models/types'

import PageContent from './components/PageContent'

export type PageContextPayload = UseFiltersPayload & {
  callbacks: {
    deleteAdAccountGroupLink: (payload: HandleCallbackActionParams) => Promise<object>,
  },
}

const defaultState = {
  showBulkCreateAdAccountGroupLinksModal: false,
}

type SetState = (state: Partial<typeof defaultState>) => void

const callbacks = (component: keyof typeof componentCallbacks, setState: SetState) => {
  const componentCallbacks = {
    BulkCreateAdAccountGroupLinksModal: {
      bulkCreateAdAccountGroupLinks: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      closeModal: () => setState({ showBulkCreateAdAccountGroupLinksModal: false }),
    }
  }

  return componentCallbacks[component] || {}
}

const Relations = () => {
  const { adAccountGroupId } = useNumericParams()

  const [state, setState] = useSetState(defaultState)
  const { showBulkCreateAdAccountGroupLinksModal } = state

  const modalContext = useModals()
  const { callbacks: { setModalData } } = modalContext

  const pageContext = useMemo(() => ({
    callbacks: {
      deleteAdAccountGroupLink: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      showBulkCreateAdAccountGroupLinksModal: (payload: { adAccountGroupLink: AdAccountGroupLinkModel }) => {
        setModalData('BulkCreateAdAccountGroupLinksModal', payload)
        setState({ showBulkCreateAdAccountGroupLinksModal: true })
      },
    },
  }), [])

  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        <PageContent adAccountGroupId={adAccountGroupId} />

        <BulkCreateAdAccountGroupLinksModal
          callbacks={callbacks('BulkCreateAdAccountGroupLinksModal', setState)}
          showModal={showBulkCreateAdAccountGroupLinksModal}
        />
      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

export default Relations
